import API from '@teamleader/api';

import { Consent, ConsentCategory, CookiesConsentPayload, StorageConsent } from './constants';

const LATEST_CONSENT_STORAGE_KEY = 'LatestConsent';

const isEnabled =
  Boolean(environment.COOKIEBOT_DOMAIN_GROUP_ID) && !document.cookie.includes('teamleader_focus_debug_mode=enabled');

const getConsent = (): Consent => {
  if (!isEnabled || !window.Cookiebot) {
    return {
      necessary: true,
      preferences: true,
      statistics: true,
      marketing: true,
    };
  }

  const bulkconsentsubmitted = window.Cookiebot.bulkconsentsubmitted ?? false;
  const consent = bulkconsentsubmitted
    ? window.Cookiebot.bulkconsent ?? window.Cookiebot.consent
    : window.Cookiebot.consent;

  const necessary = true;
  const preferences = consent?.preferences ?? false;
  const statistics = consent?.statistics ?? false;
  const marketing = consent?.marketing ?? false;

  return { necessary, preferences, statistics, marketing };
};

const getConsentTimestamp = () => {
  if (!isEnabled || !window.Cookiebot) {
    return undefined;
  }

  const bulkconsentsubmitted = window.Cookiebot.bulkconsentsubmitted ?? false;
  const bulkconsent = window.Cookiebot.bulkconsent;

  return bulkconsentsubmitted && bulkconsent && bulkconsent.utc
    ? new Date(bulkconsent.utc)
    : window.Cookiebot.consentUTC;
};

const isCategoryConsented = (category: ConsentCategory) => {
  const consent = getConsent();

  return consent[category];
};

const handleConsentReady = (accountId: number, employeeId: number) => async () => {
  const storageKey = `${accountId}_${employeeId}_${LATEST_CONSENT_STORAGE_KEY}`;
  const storageLatestConsent = localStorage.getItem(storageKey);
  const latestConsent: StorageConsent | undefined = storageLatestConsent ? JSON.parse(storageLatestConsent) : undefined;

  const consentTimestamp = getConsentTimestamp();
  const consent = getConsent();

  const currentConsent: StorageConsent = {
    timestamp: consentTimestamp?.toISOString(),
    ...consent,
  };

  if (currentConsent.timestamp !== latestConsent?.timestamp) {
    localStorage.setItem(storageKey, JSON.stringify(currentConsent));

    const payload: CookiesConsentPayload = Object.values(ConsentCategory).reduce(
      (payload, category) =>
        isCategoryConsented(category)
          ? { ...payload, allowed: [...payload.allowed, category] }
          : { ...payload, disallowed: [...payload.disallowed, category] },
      { allowed: [], disallowed: [] } as CookiesConsentPayload,
    );

    try {
      await API({
        getAccessToken: window.top?.services.authentication.getAccessToken,
        baseUrl: environment.API_URL,
      }).cookies.consent(payload);
    } catch {
      // Gracefully handle error
    }
  }
};

const initialize = (language: string, accountId: number, userId: number) => {
  if (!isEnabled) {
    return;
  }

  const script = document.createElement('script');
  script.id = 'Cookiebot';
  script.src = 'https://consent.cookiebot.com/uc.js';
  script.type = 'text/javascript';
  script.async = true;
  script.dataset.cbid = environment.COOKIEBOT_DOMAIN_GROUP_ID;
  script.dataset.culture = language.slice(0, 2);

  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);

  window.addEventListener('CookiebotOnConsentReady', handleConsentReady(accountId, userId));
};

const show = () => {
  if (!isEnabled || !window.Cookiebot || !window.Cookiebot.show) {
    return;
  }

  window.Cookiebot.show();
};

const runScripts = () => {
  if (!isEnabled || !window.Cookiebot || !window.Cookiebot.runScripts) {
    return;
  }

  window.Cookiebot.runScripts();
};

const ConsentService = {
  isEnabled,
  initialize,
  getConsent,
  isCategoryConsented,
  show,
  runScripts,
};

export default ConsentService;
